import { Coins, Mail, Mic, X } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Modal shown when a rep tries to start a call but the org is out of tokens.
 *
 * Replaces the previous one-line toast which auto-dismissed and left the rep
 * without clear next steps. Provides:
 *  - Plain-English explanation of WHY the call was blocked
 *  - Their admin's email (one-click mailto: with a pre-filled subject)
 *  - A prominent "Use In-Person mode instead" button (the fallback that
 *    doesn't consume tokens since it uses browser audio + Deepgram only)
 */
export function TokenBlockedModal({ info, onClose, onSwitchToMic }) {
  if (!info) return null;

  const admins = info.admin_emails || [];
  const primaryAdmin = admins[0];

  const mailtoBody = encodeURIComponent(
    `Hi,\n\n` +
    `I tried to start a PipeGuide meeting bot but the org is out of meeting tokens.\n\n` +
    `Could you top up the shared pool in Billing when you get a chance? It only takes a minute.\n\n` +
    `Thanks!`
  );
  const mailtoSubject = encodeURIComponent('PipeGuide — please top up meeting tokens');
  const mailtoHref = primaryAdmin
    ? `mailto:${primaryAdmin.email}?subject=${mailtoSubject}&body=${mailtoBody}`
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="token-blocked-modal-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="token-blocked-modal"
      >
        <div className="bg-amber-50 border-b border-amber-100 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-amber-600" />
            <h3 className="font-bold text-zinc-900">No meeting tokens left</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="token-blocked-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-zinc-700 leading-relaxed">
            Your personal monthly allowance is at <strong>{info.rep_balance}</strong> tokens and the
            shared top-up pool is at <strong>{info.shared_pool}</strong>. Bot-recorded calls are
            paused until someone adds tokens.
          </p>

          <div className="bg-brand-tint border border-brand-accent-muted rounded-md p-3">
            <div className="text-[10px] font-bold uppercase tracking-wide text-brand-primary mb-1">
              Quick fix
            </div>
            <p className="text-sm text-zinc-700 mb-3">
              You can still record this call live using your browser microphone — it doesn't use
              any tokens. The AI scoring works exactly the same way.
            </p>
            <Button
              onClick={() => { onSwitchToMic(); onClose(); }}
              className="w-full bg-brand-primary hover:bg-brand-hover text-white"
              data-testid="token-blocked-switch-to-mic"
            >
              <Mic className="w-4 h-4 mr-2" />
              Switch to In-Person / Phone mode
            </Button>
          </div>

          {primaryAdmin && (
            <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
              <div className="text-[10px] font-bold uppercase tracking-wide text-zinc-500 mb-1">
                Or ask your admin to top up
              </div>
              <p className="text-sm text-zinc-700 mb-3">
                Email your admin <strong>{primaryAdmin.name || primaryAdmin.email}</strong> — top-ups
                appear in your shared pool within seconds of payment.
              </p>
              <Button
                asChild
                variant="outline"
                className="w-full"
                data-testid="token-blocked-email-admin"
              >
                <a href={mailtoHref} onClick={onClose}>
                  <Mail className="w-4 h-4 mr-2" />
                  Email {primaryAdmin.email}
                </a>
              </Button>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-zinc-100 text-right bg-zinc-50/50">
          <button
            onClick={onClose}
            className="text-xs text-zinc-500 hover:text-zinc-700"
            data-testid="token-blocked-dismiss"
          >
            Dismiss for now
          </button>
        </div>
      </div>
    </div>
  );
}
